import React, { useContext, useMemo, useEffect } from 'react';
import {
  Grid,
  Button,
  Icon,
  Icons,
  FormControlLabel,
  TextField,
  Typography,
  styled,
} from '@nextiva/next-ui-components';
import { Controller, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import { debounce, isEqual } from 'lodash';

import { RevealCard } from 'components/RevealCard';
import { ActionMenu } from 'components/ActionMenu';
import { actionTypes, CallForwardContext } from 'containers/CallForwarding';
import { HuntGroupService } from 'services';
import {
  AddCondition,
  CheckboxStyles,
  CheckboxStyled,
  SelectiveTable,
} from './CallForwardSelective.styles';

const SELECTIVE_ENABLED = 'selective.enabled';
const SELECTIVE_PHONE_NUMBER = 'selective.phoneNumber';

const StyledIcon = styled(Icon)`
  color: ${({ theme }) => theme.palette.common.black};
`;

export const CallForwardSelective = ({
  fieldArrayMethods,
  huntGroupId = '',
}) => {
  const { state, dispatch } = useContext(CallForwardContext);
  const {
    clearErrors,
    getValues,
    setError,
    setValue,
    control,
    watch,
  } = useFormContext();
  const { fields, remove, update } = fieldArrayMethods;

  const watchConditionList = watch('selective.conditionList');
  const watchPhoneNumber = watch(SELECTIVE_PHONE_NUMBER);
  const watchPlayRingBack = watch('selective.playRingBack');

  const debouncedSetSelectiveDirty = useMemo(
    () =>
      debounce(
        (conditions) => {
          // Validation Note: if phone number is dirty there has to be a condition in conditionList
          // for save enabled if conditionList is dirty there has to be a phone number for save enabled
          const formValues = getValues();

          // sanitize the objects
          // The fieldArray method update function is altering the condition on update.
          // The following map functions edit the objects so that they can be tested for equality.
          const formValuesCopy = conditions.map((condition) => {
            // When fieldArray method update executes, it updates the condition and if the
            // condition doesn't have a callFrom phone number the callFrom key is removed from
            // the selective object in fields(conditions).
            if (condition.callFrom?.length === 0) delete condition.callFrom;
            // when fieldArray method update, updates a condition it adds an id key to
            // the condition.
            delete condition.id;
            return condition;
          });
          const originalFieldsCopy = state.originalFields.selective.conditionList.map(
            (condition) => {
              // Keep originalFields keys the same as fields(conditions).
              if (condition.callFrom?.length === 0) delete condition.callFrom;
              return condition;
            },
          );

          const playRingBackIsDirty =
            formValues.selective.playRingBack !==
            state.originalFields.selective.playRingBack;
          const phoneNumberIsDirty =
            formValues.selective.phoneNumber !==
            state.originalFields.selective.phoneNumber;
          const conditionListIsDirty =
            Boolean(formValues.selective.conditionList) &&
            !isEqual(formValuesCopy, originalFieldsCopy);

          if (
            phoneNumberIsDirty ||
            conditionListIsDirty ||
            playRingBackIsDirty
          ) {
            dispatch({
              type: actionTypes.DIRTY_UPDATED,
              payload: { sectionName: 'selective', dirty: true },
            });
            return;
          }

          dispatch({
            type: actionTypes.DIRTY_UPDATED,
            payload: { sectionName: 'selective', dirty: false },
          });
        },
        200,
        { trailing: true },
      ),
    [
      state.originalFields.selective.phoneNumber,
      state.originalFields.selective.playRingBack,
      state.originalFields.selective.conditionList,
      getValues,
      dispatch,
    ],
  );

  useEffect(() => {
    debouncedSetSelectiveDirty(fields);
  }, [
    fields,
    watchConditionList,
    watchPhoneNumber,
    watchPlayRingBack,
    debouncedSetSelectiveDirty,
  ]);

  const debouncedUpdateSelectiveEnabled = useMemo(
    () =>
      debounce(
        async () => {
          const checked = getValues(SELECTIVE_ENABLED);

          try {
            await HuntGroupService.toggleCallForwardSelectiveActive(
              huntGroupId,
              checked,
            );
            dispatch({
              type: actionTypes.SWITCH_TOGGLED,
              payload: { sectionName: 'selective', enabled: checked },
            });
          } catch (error) {
            let message =
              'There was an error while updating the active status for Call Forwarding (Selective). Please try again later or contact support if the issue persists.';
            if (error.message.startsWith('[Error 5024]')) {
              message =
                'Click "Save", in order to activate the selective call forwarding condition.';
            }
            dispatch({
              type: actionTypes.SNACKBAR_SET,
              payload: {
                message,
                type: 'error',
              },
            });

            setValue(SELECTIVE_ENABLED, !checked, { shouldDirty: false });
          }
        },
        500,
        { trailing: true },
      ),
    [dispatch, getValues, huntGroupId, setValue],
  );

  const handleMissingFields = (formValues) => {
    const payload = {
      message:
        'Missing required information. There must be a phone number and one active condition to enable call forwarding selective.',
      section: 'selective',
      type: 'error',
    };

    dispatch({ type: actionTypes.TOGGLE_FAILED, payload });

    if (fields.length === 0) {
      dispatch({
        type: actionTypes.TOGGLE_DRAWER,
        payload: {
          open: true,
          type: 'Add',
          updateIndex: null,
        },
      });
    }

    if (!formValues.selective.phoneNumber) {
      setError(
        SELECTIVE_PHONE_NUMBER,
        { type: 'required', message: 'Phone number is required.' },
        { shouldFocus: true },
      );
    }
  };

  const handleSwitchClick = ({ event, field }) => {
    const formValues = getValues();

    if (
      event.target.checked &&
      (!formValues.selective.phoneNumber || fields.length === 0)
    ) {
      handleMissingFields(formValues);
      return;
    }

    field.onChange(event);
    debouncedUpdateSelectiveEnabled();
  };

  const toggleConditionActive = (index) => {
    update(index, {
      ...fields[index],
      active: !fields[index].active,
    });
  };

  const editCondition = (index) => {
    dispatch({
      type: actionTypes.TOGGLE_DRAWER,
      payload: {
        open: true,
        type: 'Update',
        updateIndex: index,
      },
    });
  };

  return (
    <RevealCard
      description="Forward certain incoming calls to selected phone numbers."
      handleSwitchClick={handleSwitchClick}
      sectionName="selective"
      title="Call forward (selective)"
      tooltip="Forward calls based on specific criteria. You can forward calls based on a schedule(i.e. during lunch or after hours) or depending on who you are receiving a call from."
    >
      <Grid container>
        <Grid className="field-container" item xs={5}>
          <Controller
            control={control}
            defaultValue=""
            name={SELECTIVE_PHONE_NUMBER}
            render={({ field, fieldState }) => (
              <TextField
                fullWidth
                id="selective-phone-number"
                label="PHONE NUMBER"
                inputProps={{ maxLength: 20 }}
                // eslint-disable-next-line react/jsx-no-duplicate-props
                InputProps={{ disableUnderline: true }}
                InputLabelProps={{ shrink: true }}
                {...field}
                onChange={(event) => {
                  if (fieldState.error?.type === 'required')
                    clearErrors(SELECTIVE_PHONE_NUMBER);
                  field.onChange(event);
                }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardSelective-PhoneNumber"
              />
            )}
          />
        </Grid>
        <CheckboxStyles item xs={7}>
          <Controller
            control={control}
            defaultValue={false}
            name="selective.playRingBack"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <CheckboxStyled
                    checked={field.value}
                    disableRipple
                    {...field}
                    onChange={(event) => {
                      field.onChange(event);
                    }}
                    color="primary"
                    data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardSelective-RingBackReminderCheckBox"
                  />
                }
                label="Play ringback reminder when forwarded"
                data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardSelective-RingBackReminderLabel"
              />
            )}
          />
        </CheckboxStyles>
        <SelectiveTable item xs={12}>
          <Typography
            className="selective-header"
            component="h6"
            data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardSelective-ForwardingConditionHeader"
          >
            Forwarding Conditions
          </Typography>
          {fields.map((condition, index) => {
            return (
              <Grid className="selective-row" key={condition.name} container>
                <Grid item xs={10}>
                  <div className="selective-text">
                    {condition.name}
                    <span className="active-label">
                      {condition.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </Grid>
                <Grid item xs={2}>
                  <ActionMenu
                    active={condition.active}
                    editCondition={() => editCondition(index)}
                    removeCondition={() => {
                      setValue('selective.conditionList', fields);
                      remove(index);
                    }}
                    toggleConditionActive={() => toggleConditionActive(index)}
                  />
                </Grid>
              </Grid>
            );
          })}
        </SelectiveTable>
        <AddCondition item xs={12}>
          <Button
            onClick={() =>
              dispatch({
                type: actionTypes.TOGGLE_DRAWER,
                payload: { open: true, type: 'Add', updateIndex: null },
              })
            }
            variant="text"
            data-testid="PlatformAdminUI-TeamVoiceSettings-CallForwardSelective-AddCondition"
          >
            <StyledIcon icon={Icons.addCircle} size={18} />
            <span style={{ color: '#005FEC', marginLeft: 4 }}>
              Add condition
            </span>
          </Button>
        </AddCondition>
      </Grid>
    </RevealCard>
  );
};

CallForwardSelective.propTypes = {
  fieldArrayMethods: PropTypes.object.isRequired,
  huntGroupId: PropTypes.string.isRequired,
};
